import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, EMPTY, catchError, finalize, map, switchMap, throwError, timeout, shareReplay } from 'rxjs';
import { LoaderService } from '../../../services/loader.service';
import { environment } from '../../../../environments/environment';
import { IdleTimeoutService } from '../../../services/idle-timeout.service';
import { AuthService } from '@auth0/auth0-angular';
import { NavigationStart, Router } from '@angular/router';

const primecommproxyBaseUrl = environment.primecommproxy_BaseUrl;
const primecommBaseUrl = environment.primecomm_BaseUrl;
const prescriptionBaseUrl = environment.prescriptionBaseUrl;

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {
  private totalRequests = 0;
  private refreshToken$: Observable<string> | null = null;

  constructor(
    private _loadrServ: LoaderService,
    private _idleTimeoutServ: IdleTimeoutService,
    private auth: AuthService,
    private router: Router,
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.totalRequests = 0;
        this.hideLoader();
      }
    });
  }

  /**
   * Gets a valid token. Reads from localStorage first (instant).
   * If token is expired or missing, calls Auth0 SDK to refresh.
   * Concurrent calls share one refresh via shareReplay.
   */
  private getToken(): Observable<string> {
    const stored = this.getTokenFromStorage();

    // If token exists and is not expired, use it instantly
    if (stored && !this.isTokenExpired(stored)) {
      return of(stored);
    }

    // Token missing or expired — refresh via Auth0 SDK
    return this.refreshToken();
  }

  private getTokenFromStorage(): string {
    try {
      const token = localStorage.getItem('access_token');
      return token ? JSON.parse(token) : '';
    } catch {
      return '';
    }
  }

  /**
   * Decodes JWT expiry. Returns true if token expires within 60 seconds.
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000; // convert to ms
      return Date.now() > expiresAt - 60000; // expired or expires within 60s
    } catch {
      return true; // can't decode = treat as expired
    }
  }

  /**
   * Calls Auth0 SDK to get a fresh token.
   * Uses shareReplay so concurrent requests share one Auth0 call.
   */
  private refreshToken(): Observable<string> {
    if (!this.refreshToken$) {
      this.refreshToken$ = this.auth.getAccessTokenSilently().pipe(
        timeout(10000),
        catchError(err => {
          const auth0Error = err as { error?: string };
          if (auth0Error?.error === 'login_required' || auth0Error?.error === 'invalid_grant') {
            this.auth
              .logout({ logoutParams: { returnTo: window.location.origin } })
              .subscribe();
            return EMPTY;
          }
          return of('');
        }),
        map(token => {
          if (token) {
            localStorage.setItem('access_token', JSON.stringify(token));
          }
          this.refreshToken$ = null;
          return token;
        }),
        shareReplay(1)
      );
    }
    return this.refreshToken$;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip Auth0 domain requests to prevent circular dependency
    if (req.url.includes(environment.domain)) {
      return next.handle(req);
    }

    const elinkParams =
      localStorage.getItem('paramsData') !== 'undefined'
        ? JSON.parse(localStorage.getItem('paramsData') || '{}')
        : {};
    const elinkData =
      localStorage.getItem('eLinkData') !== 'undefined'
        ? JSON.parse(localStorage.getItem('eLinkData') || '{}')
        : {};
    const pharmacyInfo =
      localStorage.getItem('PharmacyInfo') !== 'undefined'
        ? JSON.parse(localStorage.getItem('PharmacyInfo') || '{}')
        : {};
    const userId = localStorage.getItem('myPrimeRxUserID');
    const myPrimeRxUserID = userId && userId !== undefined ? JSON.parse(userId) : null;

    const isSignatureBaseUrl = req.headers.get('isSignatureBaseUrl') === 'true';
    const isSPCBaseUrl = req.headers.get('isSPCBaseUrl') === 'true';
    const isSPCNoProxyBaseUrl = req.headers.get('isSPCNoProxyBaseUrl') === 'true';

    const finalUrl =
      req.url.startsWith('ws://') || req.url.startsWith('wss://')
        ? req.url
        : isSPCBaseUrl
        ? primecommproxyBaseUrl + req.url
        : isSignatureBaseUrl
        ? prescriptionBaseUrl + req.url
        : isSPCNoProxyBaseUrl
        ? primecommBaseUrl + req.url
        : req.url;

    const isLoadNotReq = req.headers.get('isLoadNotReq');
    const loaderMsg = req.headers.get('loadingMesssage');
    if (isLoadNotReq !== 'true') {
      this.totalRequests++;
      this.startLoader();
    }
    if (loaderMsg) {
      this.showLoaderMsg(loaderMsg);
    }

    // getToken() checks localStorage first (instant if valid), refreshes only if expired
    return this.getToken().pipe(
      switchMap(accessToken => {
        let headers = req.headers
            .set('TenantId', pharmacyInfo?.tenantId ? String(pharmacyInfo.tenantId) : '')
            .set('TimeZoneOffset', elinkData?.TimeZoneOffset ? String(elinkData.TimeZoneOffset) : '')
            .set('TenantNPI', elinkParams?.npi ? String(elinkParams.npi) : '')
            .set('UserId', myPrimeRxUserID ? String(myPrimeRxUserID) : '');

        if (accessToken) {
          headers = headers.set('Authorization', `Bearer ${accessToken}`);
        }

        const clonedReq = req.clone({
          headers,
          url: finalUrl
        });

        return next.handle(clonedReq).pipe(
          map((event: HttpEvent<any>) => {
            if (!this._idleTimeoutServ.isWarningVisible) {
              this._idleTimeoutServ.resetTimers();
            }

            if (event instanceof HttpResponse) {
              if (event.body?.ErrorMessage) {
                console.error('Server returned an ErrorMessage:', event.body.ErrorMessage);
              } else if (event.body?.[0]?.ErrorMessage) {
                console.error('Server returned array ErrorMessage:', event.body[0].ErrorMessage);
              }
            }
            return event;
          }),

          catchError((error: HttpErrorResponse) => {
            // On 401: refresh token and retry once
            if (error.status === 401 && !req.headers.has('X-Retry')) {
              return this.refreshToken().pipe(
                switchMap(newToken => {
                  if (!newToken) return throwError(() => ({ reason: '', status: 401, url: `${clonedReq.method} "${clonedReq.urlWithParams}"` }));
                  const retryReq = req.clone({
                    headers: clonedReq.headers
                      .set('Authorization', `Bearer ${newToken}`)
                      .set('X-Retry', 'true'),
                    url: finalUrl
                  });
                  return next.handle(retryReq);
                })
              );
            }

            if (error.error instanceof Blob) {
              const reader = new FileReader();
              reader.onload = (readResult: Event) => {
                try {
                  const parsed = JSON.parse((readResult.target as any).result);
                  console.error('Parsed Blob Error:', parsed);
                } catch (e) {
                  console.error('Failed to parse Blob error:', e);
                }
              };
              reader.readAsText(error.error);
            }

            switch (error.status) {
              case 0:
                console.error('Network Error / CORS issue');
                break;
              case 400:
                console.error('Bad Request (400)');
                break;
              case 401:
                console.error('Unauthorized (401)');
                break;
              case 403:
                console.error('Forbidden (403)');
                break;
              case 404:
                console.error('Not Found (404)');
                break;
              case 500:
                console.error('Internal Server Error (500)');
                break;
              default:
                console.error('Unhandled Error:', error.message);
                break;
            }

            const data = {
              reason: error.error?.reason || '',
              status: error.status,
              url: `${clonedReq.method} "${clonedReq.urlWithParams}"`
            };

            return throwError(() => data);
          })
        );
      }),
      finalize(() => {
        if (isLoadNotReq !== 'true') {
          this.decreaseRequests();
        }
        if (loaderMsg) {
          this.removeLoaderMsg();
        }
      })
    );
  }

  private startLoader(): void {
    this._loadrServ.display(true);
  }

  private hideLoader(): void {
    this._loadrServ.display(false);
  }

  private showLoaderMsg(msg: string): void {
    this._loadrServ.addMessage(msg);
  }

  private removeLoaderMsg(): void {
    this._loadrServ.addMessage('');
  }

  private decreaseRequests() {
    this.totalRequests--;
    if (this.totalRequests <= 0) {
      this.hideLoader();
      this.totalRequests = 0;
    }
  }
}
